import { useState, useEffect } from "react";
import { FALLBACK_GALLERIES } from "../data/siteData";
import LightboxModal from "./LightboxModal";
import { imageUrl } from "../lib/images";

const IMAGE_CAPTIONS = {
  "img-3502-scaled": "Hvide kølebiler med aktivt køleanlæg foran terminal",
  "gordon-elfordon425-3": "Moderne el-varevogn til emissionsvenlig bydistribution",
  "bornholmslinjen": "Varevogne ved Bornholmslinjen – dækning i hele landet",
  "1782895557011": "Mercedes-Benz kølevarevogn med aktiv temperaturkontrol",
  "shutterstock-2654580061": "Flåde af moderne hvide varebiler klar til kørsel",
  "shutterstock-2664143269": "Direkte levering af måltidskasser og fødevarer",
  "img-4141": "Distribution i byområder til privat- og erhvervsadresser",
  "shutterstock-1770826502": "Omhyggelig overdragelse af forsendelser ved døren",
  "shutterstock-2487533651": "Digital sporing og præcis temperaturstyring",
  "shutterstock-2602712947": "Stregkodemærkning og kontrolleret forsendelse",
  "shutterstock-2243452699": "Landsdækkende ruter og effektiv fremførsel",
  "shutterstock-2042952230": "Håndtering ved moderne lagerterminal og ramper",
  "shutterstock-2190533271": "Uddannede chauffører med fokus på høj service",
  "shutterstock-2629075059": "Kundeløfte: Pålidelig og forudsigelig levering"
};

function getImageCaption(url, index) {
  if (typeof url === "string") {
    for (const [key, caption] of Object.entries(IMAGE_CAPTIONS)) {
      if (url.includes(key)) return caption;
    }
  }
  return `Kølevogn og transportmateriel ${index + 1}`;
}

export default function CmsGallerySection({
  gallerySlug = "koeletransport",
  title = "Billeder fra driften",
  subtitle = "Visuel dokumentation af materiel og kapacitet"
}) {
  const [gallery, setGallery] = useState(FALLBACK_GALLERIES[gallerySlug] || null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  useEffect(() => {
    let isMounted = true;
    async function fetchGallery() {
      try {
        const res = await fetch("/_api/public/content/galleries");
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data)) {
            const match = data.find(
              (g) => g.gallery_slug === gallerySlug || g.slug?._ === gallerySlug || g.slug === gallerySlug
            );
            if (match && isMounted) {
              setGallery(match);
            }
          }
        }
      } catch {
        // Fallback already set
      }
    }
    fetchGallery();
    return () => {
      isMounted = false;
    };
  }, [gallerySlug]);

  const images = gallery?.images || FALLBACK_GALLERIES[gallerySlug]?.images || [];

  if (images.length === 0) return null;

  const openLightbox = (index) => {
    setActiveImageIndex(index);
    setLightboxOpen(true);
  };

  return (
    <section className="py-20 border-t border-slate-800/80 bg-[#091122]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded bg-[#0F766E]/20 border border-[#2DD4BF]/30 text-[#2DD4BF] text-xs font-mono uppercase tracking-widest mb-3">
              <i className="fa-solid fa-camera text-[10px]" aria-hidden="true" />
              <span>Flådedokumentation</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
              {title}
            </h2>
            <p className="mt-2 text-slate-400 text-sm sm:text-base max-w-2xl">
              {subtitle}
            </p>
          </div>
          <div className="text-xs font-mono text-slate-400 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
            <span>{images.length} fotos tilgængelige · Klik for stor visning</span>
          </div>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {images.map((imgUrl, index) => (
            <div
              key={index}
              onClick={() => openLightbox(index)}
              className="group relative h-64 sm:h-72 rounded-xl overflow-hidden bg-slate-900 border border-slate-800 hover:border-[#2DD4BF]/60 transition-all duration-300 cursor-pointer shadow-lg"
            >
              <img
                src={imageUrl(imgUrl, "width=600,quality=80,format=auto")}
                alt={getImageCaption(imgUrl, index)}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/20 to-transparent opacity-75 group-hover:opacity-90 transition-opacity" />

              {/* Hover inspect badge */}
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="w-12 h-12 rounded-full bg-[#0F766E]/90 text-white flex items-center justify-center shadow-xl transform scale-75 group-hover:scale-100 transition-transform">
                  <i className="fa-solid fa-magnifying-glass-plus text-base text-[#2DD4BF]" aria-hidden="true" />
                </div>
              </div>

              {/* Bottom label: Beskrivende billedtekst */}
              <div className="absolute bottom-3 inset-x-3 text-xs text-slate-200 pointer-events-none">
                <div className="bg-slate-950/85 backdrop-blur-xs px-2.5 py-1.5 rounded border border-slate-800 flex items-center justify-between gap-2 shadow-sm">
                  <span className="font-medium truncate text-[11px] text-slate-100">
                    {getImageCaption(imgUrl, index)}
                  </span>
                  <span className="text-[#2DD4BF] text-[10px] shrink-0 flex items-center gap-1 font-mono">
                    Vis <i className="fa-solid fa-arrow-right text-[8px]" aria-hidden="true" />
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <LightboxModal
        images={images}
        initialIndex={activeImageIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        captions={images.map((u, i) => getImageCaption(u, i))}
      />
    </section>
  );
}
